/**
 * Spin & Win Supabase Module
 * Controls the promotional Spin & Win modal, prize selection, and Supabase analytics integration.
 */
(() => {
    'use strict';
    const modal = document.getElementById('svModalSupabase');
    if (!modal) return;
    const modalCard = modal.querySelector('.sv-card');
    // Page URL and Traffic Source Filtering
    const path = window.location.pathname || "/";
    const isHome = path === '/' || path === '/en' || path === '/id' || path === '/index.html';
    const isMall = path.includes('/collections/mall');
    if (!isHome && !isMall) {
        return; // Exclude execution on other pages
    }
    const queryParams = new URLSearchParams(window.location.search);
    const reopenAfterAuth = queryParams.get('open_spin') === '1';
    const trafficSource = isHome ? 'home' : 'collection_mall';
    // Configuration and Datasets
    const supabaseUrl = modal.dataset.supabaseUrl;
    const customerId = modal.dataset.customerId;
    const customerName = modal.dataset.customerName;
    const customerEmail = modal.dataset.customerEmail;
    const customerPhone = modal.dataset.customerPhone || modal.dataset.customerDefaultPhone || '';
    const isLoggedIn = modal.dataset.loggedIn === 'true';
    const allowedSources = new Set(['button', 'auto', 'claim', 'redeem']);
    let autoOpenTimer = null;
    // Modal Lifecycle
    function openModal() {
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('sv-modal-open');
        document.body.style.overflow = 'hidden';
    }
    // Custom selector for close buttons inside this modal
    function closeModal() {
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('sv-modal-open');
        document.body.style.overflow = '';
    }
    function requestModalOpen({ source, delay = 0 } = {}) {
        if (window.CVGrowthPlanBundleSuppressMarketingPopups) return false;
        const normalizedSource = source || 'auto';
        if (!allowedSources.has(normalizedSource)) return false;
        const triggerOpen = () => openModal();
        if (delay > 0) {
            window.clearTimeout(autoOpenTimer);
            autoOpenTimer = window.setTimeout(triggerOpen, delay);
        } else {
            triggerOpen();
        }
        return true;
    }

    function scheduleAutoOpen() {
        requestModalOpen({ source: 'auto', delay: 5000 });
    }
    // Auto trigger popup after 5 seconds on page load
    window.addEventListener('DOMContentLoaded', () => {
        if (reopenAfterAuth && isLoggedIn) {
            requestModalOpen({ source: 'auto' });

            // Remove the one-time flag so a refresh does not reopen the modal.
            queryParams.delete('open_spin');
            const cleanSearch = queryParams.toString();
            const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash}`;
            window.history.replaceState({}, '', cleanUrl);
            return;
        }

        if (document.body.classList.contains('cv-growth-popup-open')) {
            return;
        }

        scheduleAutoOpen();
    });

    document.addEventListener('cv:growth-plan-popup:opened', () => {
        window.clearTimeout(autoOpenTimer);
        autoOpenTimer = null;
    });

    document.addEventListener('cv:growth-plan-popup:closed', () => {
        if (!window.CVGrowthPlanBundleSuppressMarketingPopups && modal.getAttribute('aria-hidden') !== 'false') {
            scheduleAutoOpen();
        }
    });
    document.addEventListener('cv:growth-plan-bundle:suppress-marketing', () => {
        window.clearTimeout(autoOpenTimer);
        autoOpenTimer = null;
        closeModal();
    });
    // Event Listeners
    document.querySelectorAll('.open-btn').forEach(btn => {
        btn.addEventListener('click', () => requestModalOpen({ source: 'button' }));
    });
    document.getElementById('svCloseSupabase')?.addEventListener('click', closeModal);
    modal.querySelector('.sv-backdrop')?.addEventListener('click', closeModal);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeModal();
    });
    // Spin Logic
    const wheel = document.getElementById('wheelSupabase');
    const spinBtn = document.getElementById('spinBtnSupabase');
    const spinLabel = spinBtn?.querySelector('.sv-spin-label');
    const resultEl = document.getElementById('sv-result-supabase');
    const defaultResultText = resultEl ? resultEl.textContent : '';
    // Prize definition (5 visual slices).
    // centerAngle is measured clockwise from the 12 o'clock pointer in the artwork.
    const prizes = [
        { label: 'Voucher 15% Off', tag: 'spin15off', code: 'SPIN15', weight: 1, centerAngle: 36 },
        { label: 'Voucher 20% Off', tag: 'spin20off', code: 'SPIN20', weight: 1, centerAngle: 108 },
        { label: 'Voucher 25% Off', tag: 'spin25off', code: 'SPIN25', weight: 1, centerAngle: 180 },
        { label: 'Voucher 30% Off', tag: 'spin30off', code: 'SPIN30', weight: 1, centerAngle: 252 },
        { label: 'Voucher 10% Off', tag: 'spin10off', code: 'SPIN10', weight: 1, centerAngle: 324 }
    ];
    let spinning = false;
    let hasSpun = false;
    async function sendReward(prize) {
        if (!customerId || !supabaseUrl) return;

        const edgeFunctionUrl = `${supabaseUrl}/functions/v1/update-spin-reward`;
        try {
            const response = await fetch(edgeFunctionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    shopify_customer_id: customerId,
                    name: customerName,
                    email: customerEmail,
                    phone: customerPhone,
                    reward_won: prize.tag,
                    traffic_source: trafficSource
                })
            });

            if (response.status === 409) {
                // The customer has already spun — this should not happen because
                // Liquid is already filtered, but it can happen if there is a race condition/cache
                console.warn('Customer already has hasspin tag.');
                return;
            }

            if (!response.ok) {
                console.error('Error synchronizing reward tags:', await response.text());
            }
        } catch (err) {
            console.error('Network error calling reward sync endpoint:', err);
        }
    }
    const pickWeightedIndex = (items) => {
        const totalWeight = items.reduce((sum, item) => sum + (Number(item?.weight) || 0), 0);
        if (totalWeight <= 0) return Math.floor(Math.random() * items.length);
        let threshold = Math.random() * totalWeight;
        for (let i = 0; i < items.length; i++) {
            const weight = Number(items[i]?.weight) || 0;
            if (threshold < weight) return i;
            threshold -= weight;
        }
        return items.length - 1;
    };
    function spin() {
        if (spinning || hasSpun || !spinBtn || !isLoggedIn) return;
        if (modalCard) {
            const currentCardHeight = modalCard.getBoundingClientRect().height;
            if (currentCardHeight > 0) {
                modalCard.style.setProperty('--sv-spin-card-height', `${Math.ceil(currentCardHeight)}px`);
                modalCard.classList.add('sv-card--height-locked');
            }
        }
        spinning = true;
        hasSpun = true;
        spinBtn.disabled = true;
        spinBtn.setAttribute('aria-busy', 'true');
        if (spinLabel) spinLabel.textContent = 'Memutar...';
        modal.dataset.spinState = 'spinning';
        if (resultEl) resultEl.textContent = 'Memutar...';
        const targetIndex = pickWeightedIndex(prizes);
        const prize = prizes[targetIndex];
        // Keep the pointer inside the central safe area of the selected slice.
        const safeJitter = (Math.random() * 14) - 7;
        const turns = 5 + Math.floor(Math.random() * 3);
        const targetGlobalAngle = (turns * 360) - prize.centerAngle + safeJitter;
        const duration = 4200 + Math.random() * 1200;
        wheel.style.setProperty('--duration', duration + 'ms');
        wheel.style.setProperty('--angle', targetGlobalAngle + 'deg');
        const onDone = () => {
            wheel.removeEventListener('transitionend', onDone);

            if (resultEl) {
                const discountUrl = `/discount/${encodeURIComponent(prize.code)}?redirect=/collections/mall`;
                resultEl.innerHTML = `
                    <div class="sv-reward-success">
                        <p class="sv-reward-title">🎉 Selamat! Anda mendapatkan:</p>
                        <p class="sv-reward-label">${prize.label}</p>
                        <div class="sv-coupon-code-box">
                            <span class="sv-coupon-code" id="svCouponCode">${prize.code}</span>
                            <button type="button" class="sv-btn-copy" id="svCopyBtn">Salin</button>
                        </div>
                        <a class="sv-btn-use" href="${discountUrl}">Gunakan Sekarang</a>
                        <button type="button" class="sv-btn-bundle" id="svBundleBtn">LIHAT BUNDLE + 3 GRATIS</button>
                    </div>
                `;
                modal.classList.add('sv-has-result');
                modal.dataset.spinState = 'result';
                // Add Copy functionality
                document.getElementById('svCopyBtn')?.addEventListener('click', () => {
                    const code = document.getElementById('svCouponCode')?.textContent;
                    if (code) {
                        navigator.clipboard.writeText(code).then(() => {
                            const btn = document.getElementById('svCopyBtn');
                            if (btn) {
                                btn.textContent = 'Tersalin!';
                                btn.classList.add('copied');
                                setTimeout(() => {
                                    btn.textContent = 'Salin';
                                    btn.classList.remove('copied');
                                }, 2000);
                            }
                        });
                    }
                });
                document.getElementById('svBundleBtn')?.addEventListener('click', () => {
                    document.dispatchEvent(new CustomEvent('cv:growth-plan-bundle:open'));
                });
            }

            sendReward(prize);
            spinning = false;
            spinBtn.setAttribute('aria-busy', 'false');
        };
        wheel.addEventListener('transitionend', onDone);
    }
    spinBtn?.addEventListener('click', spin);
})();
